import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PerformanceAnalytics = ({ analyticsData }) => {
  const [activeTab, setActiveTab] = useState('execution');

  const tabs = [
    { id: 'execution', label: 'Execution Times', icon: 'Clock' },
    { id: 'accuracy', label: 'Code Quality', icon: 'CheckCircle' },
    { id: 'skills', label: 'Skill Progress', icon: 'TrendingUp' }
  ];

  const COLORS = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444'];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-subtle rounded-lg p-3 shadow-elevation-2">
          <p className="text-sm font-medium text-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry?.color }}>
              {entry?.name}: {entry?.value}
              {entry?.dataKey === 'executionTime' && 'ms'}
              {entry?.dataKey === 'accuracy' && '%'}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderExecutionChart = () => (
    <div className="space-y-6">
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={analyticsData?.executionTrends}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="date" 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
            />
            <YAxis 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="executionTime"
              stroke="var(--color-primary)"
              strokeWidth={2}
              dot={{ fill: 'var(--color-primary)', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: 'var(--color-primary)', strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Zap" size={16} color="var(--color-success)" />
            <span className="text-sm font-medium text-foreground">Best Time</span>
          </div>
          <div className="text-2xl font-bold text-success">
            {analyticsData?.bestExecutionTime}ms
          </div>
        </div>
        
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="BarChart3" size={16} color="var(--color-primary)" />
            <span className="text-sm font-medium text-foreground">Average</span>
          </div>
          <div className="text-2xl font-bold text-primary">
            {analyticsData?.avgExecutionTime}ms
          </div>
        </div>
        
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="TrendingDown" size={16} color="var(--color-warning)" />
            <span className="text-sm font-medium text-foreground">Improvement</span>
          </div>
          <div className="text-2xl font-bold text-warning">
            -{analyticsData?.improvementPercentage}%
          </div>
        </div>
      </div>
    </div>
  );

  const renderAccuracyChart = () => (
    <div className="space-y-6">
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={analyticsData?.accuracyTrends}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="category" 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
            />
            <YAxis 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey="accuracy"
              fill="var(--color-secondary)"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {analyticsData?.codeQualityMetrics?.map((metric, index) => (
          <div key={index} className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name={metric?.icon} size={16} color={metric?.color} />
              <span className="text-sm font-medium text-foreground">{metric?.label}</span>
            </div>
            <div className="text-xl font-bold" style={{ color: metric?.color }}>
              {metric?.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderSkillsChart = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="h-80">
          <h3 className="text-lg font-semibold text-foreground mb-4">Skill Distribution</h3>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={analyticsData?.skillDistribution}
                cx="50%"
                cy="50%"
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(0)}%`}
              >
                {analyticsData?.skillDistribution?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground">Skill Progress</h3>
          {analyticsData?.skillProgress?.map((skill, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">{skill?.name}</span>
                <span className="text-sm text-muted-foreground">{skill?.level}/5</span>
              </div>
              <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-500"
                  style={{ width: `${(skill?.level / 5) * 100}%` }}
                />
              </div>
              <div className="text-xs text-muted-foreground">
                {skill?.description}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-card rounded-lg border border-subtle p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Performance Analytics</h2>
        <Button
          variant="outline"
          size="sm"
          iconName="Download"
          iconPosition="left"
        >
          Export Data
        </Button>
      </div>
      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-subtle">
        {tabs?.map((tab) => (
          <Button
            key={tab?.id}
            variant={activeTab === tab?.id ? "default" : "ghost"}
            size="sm"
            iconName={tab?.icon}
            iconPosition="left"
            onClick={() => setActiveTab(tab?.id)}
            className="mb-2"
          >
            {tab?.label}
          </Button>
        ))}
      </div>
      {/* Chart Content */}
      <div className="min-h-96">
        {activeTab === 'execution' && renderExecutionChart()}
        {activeTab === 'accuracy' && renderAccuracyChart()}
        {activeTab === 'skills' && renderSkillsChart()}
      </div>
    </div>
  );
};

export default PerformanceAnalytics;